import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { HighlightButton } from '../Highlights';
import Lightbox from '@mlb/react-patterns/v4/components/Lightbox';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useTracking from '@gameday/utils/hooks/useTracking';
import VideoPlayer from '@gameday/components/VideoPlayer/VideoPlayer';
import { useSelector } from 'react-redux';
import { getHighlights } from '../store/selectors/highlightsSelector';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import GDBREAKPOINTS from '@gameday/components/themes/breakpoints';
import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';
const { SM_MIN } = GDBREAKPOINTS;
const { PILL_SOLID } = ButtonShapes;

const highlightTrackingTypes = {
    recap: 'Game Recap',
    condensedGame: 'Condensed Game'
};

const HighlightVideo = ({ isOpen, onClose, highlight }) => {
    const {
        title = '',
        duration = '',
        id,
        templateUrl = '',
        slug,
        playbacks
    } = highlight || {};
    const width = useMedia({ minWidth: SM_MIN }) ? '600px' : '375px';
    const videoPlayerProps = {
        title,
        duration,
        playbackContainerId: 'highlights-' + id,
        id,
        templateUrl,
        slug,
        playbacks,
        showMetaInfo: true,
        width,
        playerContext: highlightTrackingTypes[highlight?.videoType],
        autoPlay: true,
        autoPlayTracking: false
    };
    return (
        <Lightbox isOpen={isOpen} onClose={onClose} >
            <VideoPlayer
                {...videoPlayerProps}
            />
        </Lightbox>
    );
};
HighlightVideo.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    highlight: PropTypes.object,
    useNativePlayer: PropTypes.bool
};

export const HighlightsVideoList = (props) => {
    const { nativeVideoPlayer, highlightVideoData } = useSelector(getHighlights);
    const translate = useTranslate();
    const Tracking = useTracking();
    const [currentHighlight, setCurrentHighlight] = useState(null);
    const [isHighlightOpen, setIsHighlightOpen] = useState(false);
    const highlightLabels = {
        gameRecap: translate('button_game_recap', 'Game Recap'),
        condensedGame: translate('button_condensed_game', 'Condensed Game')
    };
    const trackHighlight = useCallback((trackData) => {
        const cfg = {
            componentName: ['Wrap', 'Highlights'],
            text: trackData || '',
            activity: `${trackData} Click`
        };
        Tracking.track.track_click(cfg);
    }, [Tracking]);

    const openHighlightVideo = useCallback((e, highlight) => {
        setCurrentHighlight(highlight);
        setIsHighlightOpen(true);
        trackHighlight(e?.currentTarget?.innerText);
    }, [setCurrentHighlight, setIsHighlightOpen]);

    const closeHighlightVideo = useCallback(() => {
        setCurrentHighlight(null);
        setIsHighlightOpen(false);
    }, [setCurrentHighlight, setIsHighlightOpen]);

    const highlightVideos = useMemo(() => {
        const highlightVideoTypes = Object.keys(highlightVideoData);
        return highlightVideoTypes.reverse().filter(highlight => !!highlightVideoData[highlight]).map((highlightType, index) => {
            return <HighlightButton
                key={`highlights-${highlightType}-${index}`}
                label={highlightLabels[highlightType]}
                shape={PILL_SOLID}
                iconName={'play'}
                iconLeft
                isMobile={props.isMobile}
                handlers={{
                    onClick: (e) => openHighlightVideo(e, highlightVideoData[highlightType])
                }}
            />;
        }
        );
    }, [highlightVideoData, props.isMobile]);

    return (
        <>
            {highlightVideos}
            {(currentHighlight && !nativeVideoPlayer) && <HighlightVideo isOpen={isHighlightOpen} onClose={closeHighlightVideo} highlight={currentHighlight}/>}
        </>
    );
};

HighlightsVideoList.propTypes = {
    isMobile: PropTypes.bool
};
